import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { IconCheck, IconCopy, IconDownload, IconX } from '@shared/ui/icons';
import type { Export1cResult } from '@shared/api';
import { formatMillions, formatMoney } from './financierFormat';

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
  padding: 16px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;

  &:focus {
    outline: none;
  }
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
`;

const SummaryBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SkippedNote = styled.div`
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.status.warning};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const CodePreview = styled.pre`
  margin: 0;
  padding: 14px;
  background: #0f172a;
  color: #38bdf8;
  font-family: 'JetBrains Mono', 'Fira Code', Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  border-radius: ${({ theme }) => theme.radius.md};
  max-height: 340px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  border: 1px solid rgba(56, 189, 248, 0.2);
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

interface Props {
  open: boolean;
  onClose: () => void;
  exportResult: Export1cResult | null;
}

export function Export1CModal({ open, onClose, exportResult }: Props) {
  const [copied, setCopied] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const downloadBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        downloadBtnRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!open || !exportResult) return null;

  const { file_content, requests_count, total_amount } = exportResult;
  const skippedTaxes = exportResult.skipped_tax_requests ?? [];

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(file_content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback if clipboard api fails
      const textArea = document.createElement('textarea');
      textArea.value = file_content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function handleDownload() {
    const blob = new Blob([file_content], { type: 'text/plain;charset=windows-1251' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `1c_payment_orders_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        ref={modalContentRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-1c-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="export-1c-modal-title">Выгрузка в 1С:Клиент-Банк (v1.03)</ModalTitle>
          <Button $variant="ghost" $size="sm" onClick={onClose} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </ModalHeader>

        <Container>
          <SummaryBar>
            <div>
              Платёжных поручений: <b>{requests_count} шт.</b>
            </div>
            <div>
              Сумма выгрузки: <b>{formatMoney(total_amount)}</b> ({formatMillions(total_amount)})
            </div>
          </SummaryBar>

          {skippedTaxes.length > 0 && (
            <SkippedNote>
              Не выгружены налоги ЕНС — в файле нет полей бюджетного платежа, проведите их в
              банк-клиенте: {skippedTaxes.join('; ')}
            </SkippedNote>
          )}

          <CodePreview>{file_content}</CodePreview>

          <ActionsRow>
            <Button $variant="secondary" onClick={handleCopy}>
              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              {copied ? 'Скопировано' : 'Скопировать в буфер'}
            </Button>
            <Button ref={downloadBtnRef} $variant="primary" onClick={handleDownload}>
              <IconDownload size={16} />
              Скачать файл .txt
            </Button>
            <Button $variant="ghost" onClick={onClose}>
              Закрыть
            </Button>
          </ActionsRow>
        </Container>
      </ModalContent>
    </ModalOverlay>
  );
}
